import { isInTauriShell } from './detector'
import { invokeTauri } from './bridge'
import type { PrintJobRequest, PrintJobResult, PrinterInfo } from './types'

/**
 * Print an image: in Tauri shell uses native print_job; in browser uses window.print or print-js (caller's responsibility to inject).
 */
export async function printImage(request: PrintJobRequest): Promise<PrintJobResult> {
  if (isInTauriShell()) {
    try {
      // Tauri commands expect args keyed by parameter name; this command has a single param "request"
      const result = await invokeTauri<{
        jobId: string
        success: boolean
        error?: string
        errorCode?: string
      }>('print_job', {
        request: {
          jobId: request.jobId,
          imageData: request.imageData,
          printerName: request.printerName ?? null,
          printerIp: request.printerIp ?? null,
          copies: request.copies,
          orientation: request.orientation,
          paperSize: request.paperSize ?? null,
          margins: request.margins ?? null,
          dpi: request.dpi ?? null,
          scaleFit: request.scaleFit ?? 'fit',
        },
      })
      return {
        jobId: result.jobId,
        success: result.success,
        error: result.error,
        errorCode: result.errorCode,
      }
    } catch (e) {
      return {
        jobId: request.jobId,
        success: false,
        error: e instanceof Error ? e.message : String(e),
      }
    }
  }

  // Browser: return a result that tells the caller to use existing print flow (PrintUtils.sendToPrinter / print-js)
  return {
    jobId: request.jobId,
    success: false,
    error: 'Use browser print flow (PrintUtils or print-js)',
  }
}

/**
 * List printers: in Tauri shell uses native list_printers; in browser returns empty.
 */
export async function listPrinters(): Promise<PrinterInfo[]> {
  if (!isInTauriShell()) return []

  try {
    const list = await invokeTauri<Array<{
      name: string
      is_default: boolean
      is_available: boolean
      location?: string
      model?: string
    }>>('list_printers')
    return list.map((p) => ({
      name: p.name,
      isDefault: p.is_default,
      isAvailable: p.is_available,
      location: p.location,
      model: p.model,
    }))
  } catch {
    return []
  }
}
